import React from 'react';
import { Box, Flex } from '@withjoy/joykit';
import { useIsMobileOrTablet, useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { ChoiceGroup, QuantityDialogProvider } from '@apps/card/routes/CardCustomizer';
import type { StationeryProductSelector } from '../../ProductDetailsPage.types';
import { Breadcrumbs } from '../Breadcrumbs';
import { CustomizeNowButton } from '../../CustomizeNowButton';
import { Layout } from '../Layout/Layout';
import { TitleBlock } from '../TitleBlock';
import { Skeleton } from '@shared/components/Skeleton';
import { useFoilConfig } from '@apps/card/hooks/useFoilConfig';
import { useDisplayPrice } from '../../useDisplayPrice';
import { RelatedItems } from '../RelatedItems';
import { ProductSelector } from '../ProductSelector';

const CONTENT_HORIZONTAL_PADDING = { _: 6, sm2: 7 };

interface PdpLayoutProps {
  themeId: string;
  designName: string;
  templateCategory: StationeryTemplateCategoryEnum;
  showCustomizeButtonInHeader: boolean;
  onCustomizeNowClick: () => void;
  onSecondaryCtaClick?: () => void;
  secondaryCtaLabel?: string;
  isCreatingDraft: boolean;
  productSelector: StationeryProductSelector;
  subtotal?: string;
  previewPane: () => JSX.Element;
  customizationPane: () => JSX.Element;
  disableCustomizeNowButton?: boolean;
  isPremium?: boolean;
  isFoil?: boolean;
  /**
   * Narrows `RelatedItems` to the colorway being viewed. Only the formats that
   * actually show colorways pass it; basic digital leaves it undefined.
   */
  selectedColorwayId?: string;
}
export const PdpLayout = (props: PdpLayoutProps) => {
  const {
    themeId,
    disableCustomizeNowButton,
    isCreatingDraft,
    previewPane,
    customizationPane,
    designName,
    templateCategory,
    onCustomizeNowClick,
    showCustomizeButtonInHeader,
    productSelector,
    isFoil = false,
    selectedColorwayId,
  } = props;

  const isMobile = useIsMobileScreen();
  const isMobileOrTablet = useIsMobileOrTablet();
  const { pricePerCardInMinorUnits: foilPrice } = useFoilConfig();
  const displayPrice = useDisplayPrice({ taylor: isFoil ? foilPrice : 0 });

  const title = () => (
    <TitleBlock
      title={designName}
      isFoil={isFoil}
      format={productSelector.cardFormat}
    />
  );

  return (
    <QuantityDialogProvider isInsidePDP>
      <Flex
        justifyContent="center"
        width="100%"
      >
        <Layout.Container
          display="flex"
          flexDirection="column"
          alignItems="center"
          height="100%"
          width={1280}
          maxWidth="100%"
        >
          <Box
            width="100%"
            marginBottom={{ _: 6, sm2: 0 }}
            marginTop={{ _: 6, sm2: 0 }}
            paddingX={CONTENT_HORIZONTAL_PADDING}
          >
            <Layout.Header
              alignItems="center"
              paddingY={{ sm2: 4 }}
              display="flex"
              justifyContent="space-between"
              paddingRight={showCustomizeButtonInHeader ? 60 : undefined}
            >
              <Breadcrumbs
                designName={designName}
                templateCategory={templateCategory}
              />
              {showCustomizeButtonInHeader && (
                <CustomizeNowButton
                  disabled={disableCustomizeNowButton}
                  onCustomizeClick={onCustomizeNowClick}
                  isLoading={isCreatingDraft}
                />
              )}
            </Layout.Header>
          </Box>
          <Box
            width="100%"
            paddingX={CONTENT_HORIZONTAL_PADDING}
            marginBottom={60}
          >
            <Layout.Body>
              <Layout.LeftPanel>
                {isMobile && title()}
                {previewPane()}
              </Layout.LeftPanel>
              <Layout.RightPanel>
                <Flex
                  flexDirection="column"
                  paddingRight={{ md: 8 }}
                  marginTop={{ _: 0, sm2: 0 }}
                >
                  {!isMobile && title()}
                  {productSelector && (
                    <ProductSelector
                      productSelector={productSelector}
                      pricing={displayPrice}
                    />
                  )}
                  {customizationPane()}
                </Flex>
              </Layout.RightPanel>
            </Layout.Body>
          </Box>
          <RelatedItems
            category={templateCategory}
            themeId={themeId}
            selectedColorwayId={selectedColorwayId}
          />
          {isMobileOrTablet && (
            <Box
              justifyContent="center"
              borderTop="1px solid"
              borderTopColor="mono3"
              paddingX={CONTENT_HORIZONTAL_PADDING}
              paddingY={6}
              position="sticky"
              bottom={0}
              width="100%"
              backgroundColor="white"
              // 10 - to be over the card preview (it has zIndex=5)
              zIndex="docked"
            >
              <CustomizeNowButton
                disabled={disableCustomizeNowButton}
                fullWidth
                onCustomizeClick={onCustomizeNowClick}
                isLoading={isCreatingDraft}
              />
            </Box>
          )}
        </Layout.Container>
      </Flex>
    </QuantityDialogProvider>
  );
};

const PdpLayoutSkeleton = () => {
  const isMobile = useIsMobileScreen();
  return (
    <Flex
      justifyContent="center"
      width="100%"
    >
      <Layout.Container
        display="flex"
        flexDirection="column"
        alignItems="center"
        height="100%"
        width={1280}
        maxWidth="100%"
      >
        <Box
          width="100%"
          marginBottom={{ _: 6, sm2: 0 }}
          marginTop={{ _: 6, sm2: 0 }}
          paddingX={CONTENT_HORIZONTAL_PADDING}
        >
          <Layout.Header
            alignItems="center"
            paddingY={{ sm2: 4 }}
            display="flex"
            justifyContent="space-between"
          >
            <Breadcrumbs.Skeleton />
          </Layout.Header>
        </Box>
        <Box
          width="100%"
          paddingX={CONTENT_HORIZONTAL_PADDING}
          marginBottom={60}
        >
          <Layout.Body>
            <Layout.LeftPanel>
              {isMobile && <TitleBlock.Skeleton />}
              <Skeleton aspectRatio={'1'} />
            </Layout.LeftPanel>
            <Layout.RightPanel>
              <Flex
                flexDirection="column"
                paddingRight={{ md: 8 }}
                marginTop={{ _: 0, sm2: 0 }}
              >
                {!isMobile && <TitleBlock.Skeleton />}
                <Flex
                  flexDirection="column"
                  rowGap={7}
                >
                  <ChoiceGroup.Skeleton
                    minHeight={55}
                    choices={2}
                  />
                  <ChoiceGroup.Skeleton
                    choices={0}
                    disablePriceSkeleton
                  />
                  <ChoiceGroup.Skeleton choices={4} />
                  <ChoiceGroup.Skeleton choices={3} />
                  <ChoiceGroup.Skeleton
                    choices={1}
                    columns={1}
                    minHeight={50}
                  />
                  <ChoiceGroup.Skeleton
                    choices={1}
                    columns={1}
                    minHeight={48}
                    disablePriceSkeleton
                  />
                </Flex>
              </Flex>
            </Layout.RightPanel>
          </Layout.Body>
        </Box>
      </Layout.Container>
    </Flex>
  );
};

PdpLayout.Skeleton = PdpLayoutSkeleton;
